import { Metadata } from "next";
import { MetaDataInterface } from "../types/interfaces";
import { isWhatuni } from "./commonFunction";
import { getGAPageName } from "../graphQL/search-results";
import { graphQlFetchFunction } from "../server-actions/server-action";
import { getNextPrevUrls } from "@packages/shared-components/services/utils";
import { KeyNames } from "./filters/filterJson";
const keyName = KeyNames();
export function getMetaDetailsObject(
  metaData: MetaDataInterface,
  metaparams?: any,
  noindex?: boolean,
): Metadata {
  const customDomain = getCustomDomain();
  const urlParam = metaparams?.searchparams ? new URLSearchParams(metaparams.searchparams) : null;
  const hasPaginationInUrl = urlParam?.get(keyName?.pageNumber);
  if (urlParam && !hasPaginationInUrl) {
    urlParam.set(keyName.pageNumber, "1");
  }
  const prevUrl = urlParam ? getNextPrevUrls(
    metaparams?.pathParams,
    urlParam.toString(),
    "prev",
    Number(metaData?.hasPagination),
  ) : null;
  const nextUrl = urlParam ? getNextPrevUrls(
    metaparams?.pathParams,
    urlParam.toString(),
    "next",
    Number(metaData?.hasPagination),
  ) : null;
  const currentPage = Number(urlParam?.get(keyName?.pageNumber) || 1);
  const hasPagination = Number(metaData?.hasPagination) > 1;
  const fullPrevUrl = prevUrl ? `${customDomain}${prevUrl}` : "";
  const fullNextUrl = nextUrl ? `${customDomain}${nextUrl}` : "";

  const alternatesObj: any = {
    ...(noindex
      ? {}
      : metaData?.canonical
        ? { canonical: metaData.canonical }
        : {}),
    ...(hasPagination && currentPage > 1 && prevUrl && { prev: fullPrevUrl }),
    ...(hasPagination && nextUrl && { next: fullNextUrl }),
  };
  const metadata: Metadata = {
    alternates: alternatesObj,
    title: metaData?.title || "Default Title",
    description: metaData?.description || "Default Description",
    robots: metaData?.indexation || "noindex, nofollow",
    keywords: metaData?.keyword || [],
    // Open Graph metadata - properly structured for Next.js
    openGraph: {
      title: metaData?.og_title || metaData?.title || "Default Title",
      description:
        metaData?.og_description ||
        metaData?.description ||
        "Default Description",
      url: metaData?.og_canonical || customDomain,
      type: "website",
      images: [
        {
          url: isWhatuni()
            ? "https://images.ctfassets.net/szez98lehkfm/UEsONfx1Q29FkoafrRlPT/e89b566373b65e6a6cfa1f575986566c/whatuni_logo.svg"
            : "https://images.ctfassets.net/szez98lehkfm/6Z2XBvZNThCE23P5umA60L/a64ba065a299e24679fb163a85032cf7/POSTGRADUATE_SEARCH_RGB.svg",
        },
      ],
    },
    // Twitter metadata - properly structured for Next.js
    twitter: {
      card: "summary",
      creator: process.env.PROJECT === "Whatuni" ? "@whatuni" : "@pgssearch",
      title: metaData?.twitter_titile || metaData?.title || "Default Title",
      description:
        metaData?.twitter_description ||
        metaData?.description ||
        "Default Description",
      images: [
        "https://images.ctfassets.net/szez98lehkfm/UEsONfx1Q29FkoafrRlPT/e89b566373b65e6a6cfa1f575986566c/whatuni_logo.svg",
      ],
    },
    // Custom meta tags that don't have dedicated fields
    other: {
      "fb:app_id":
        process.env.PROJECT === "Whatuni"
          ? "374120612681083"
          : "506806236030900",
      "apple-itunes-app":
        process.env.NEXT_PUBLIC_CLEARING_FLAG?.toLowerCase() === "on"
          ? ""
          : "app-id=1267341390",
      "google-play-app":
        process.env.NEXT_PUBLIC_CLEARING_FLAG?.toLowerCase() === "on"
          ? ""
          : "app-id=com.hotcourses.group.wuapp",
      ...(hasPagination &&
        currentPage > 1 &&
        fullPrevUrl && { "pagination:prev": fullPrevUrl }),
      ...(hasPagination && fullNextUrl && { "pagination:next": fullNextUrl }),
    },
  };

  return metadata;
}

export function getCustomDomain() {
  const siteDomainName = isWhatuni() ? "whatuni" : "postgraduatesearch";
  switch (process.env.NEXT_PUBLIC_ENVIRONMENT) {
    case "dev":
      return `https://mdev.dev.aws.${siteDomainName}.com`;
    case "stg":
      return `https://mtest.test.aws.${siteDomainName}.com`;
    case "prd":
      return `https://www.${siteDomainName}.com`;
    default:
      return isWhatuni() ? "http://localhost:3000" : "http://localhost:3001";
  }
}

export function getQualTextForQualId(qualId: string) {
  switch (qualId?.toUpperCase()) {
    case "M":
      return "Undergraduate";
    case "L":
      return "Postgraduate";
    case "A":
      return "Foundation degree";
    case "T":
      return "Access and foundation";
    case "N":
      return "HND/HNC";
    default:
      return "";
  }
}

export async function getPageNameGACS(fromPage: string) {
  const query = getGAPageName(
    (fromPage == "SR" ? "search-result" : "product-result") +
      `-${process.env.PROJECT}`,
  );
  let contentfulMetadata = await graphQlFetchFunction(query, false);
  let pageNameGACS =
    contentfulMetadata?.data?.pageTemplateDynamicPageCollection?.items[0]
      ?.gaPageName;
  return pageNameGACS;
}

export function stringifySearchParamsPreserveFlags(params: {
  [key: string]: string | string[] | undefined;
}) {
  return Object.entries(params)
    .map(([key, value]: any) =>
      value === "" || value === undefined
        ? key
        : `${key}=${encodeURIComponent(value)}`,
    )
    .join("&");
}
